export class SharedDataModels {}

export type DaysOfWeekType = {
    id: number;
    name: string;
};

export const DayOfWeekConstants: ReadonlyArray<DaysOfWeekType> = [
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
    { id: 7, name: "Sunday" },
];

export const DEFAULT_VALUES = {
    TASK_MASTER_API_PAGE_SIZE: 25,
    TASK_MASTER_API_PAGE_NUMBER: 0,
    PAGE_NUMBER_FOR_PAGINATION: 1,
};

export const TaskMasterResultEntriesOptions: CustomDropdownOption[] = [
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: 200, label: "200" },
];

export class SprintsObjectResponse {
    sprintId: number;
    sprintTitle: string;
}

export class SprintsGroupObject {
    teamName: string;
    teamId: number;
    displayName: string;
    sprintList: SprintsObjectResponse[];
}

export class SprintsTreeObject {
    name: string;
    children: SprintsGroupObject[];
}

export class SprintsTreeNode {
    name: string;
    expandable?: boolean;
    id: number;
    selectable?: boolean;
    sprint?: SprintsObjectResponse; // Add sprint if needed for node data
    children?: SprintsTreeNode[];
}

export class SearchCriteria {
    searchTerm: string; // @Not null
    orgId: number | null;
    buId: number | null;
    teamId: number | null;
    projectId: number | null;
    workflowStatuses: string[] | null; // Specify a more specific type if possible
    workflowStatusIds: null; // Always null, consider omitting or explaining its use
    accountIdAssigned: number | null;
    taskProgressSystems: string[] | null; // Specify a more specific type if possible
    sprintId: number | null;
    taskNumbersToSkip: string[];
    labelIds: number[] | null; // List<Long> implies an array of numbers in TypeScript
    currentActivityIndicator: number | null;
    currentlyScheduledTaskIndicator: boolean | null;
    includePersonalTasks?: boolean | null;
    epicId: number | null;
    isStarred?: boolean | null = null;
    starredBy?: number[] | null = null;

    constructor(params: Partial<SearchCriteria> = {}) {
        Object.assign(this, params);
    }
}

export interface CustomDropdownOption {
    value: any;
    label: string;
    labelExtended?: string;
    toolTip?: string;
    disabled?: boolean;
    isDisabled?: boolean;
    expStartDate?: string;
    expEndDate?: string;
    userNameLabel?: string;
    profileImage?: string | null;
    extras?: any; // user object for users dropdown
    userId?: number; // user id for users dropdown
}

export enum ViewModesEnum {
    MY_DASHBOARD = 3,
    MY_VIEW = 2,
    MANAGER_VIEW = 1,
}

export const VIEW_MODES_DROPDOWN_OPTIONS: CustomDropdownOption[] = [
    { value: ViewModesEnum.MY_DASHBOARD, label: "My Dashboard" },
    { value: ViewModesEnum.MY_VIEW, label: "My View" },
    { value: ViewModesEnum.MANAGER_VIEW, label: "Overall Dashboard" },
];

export enum ProgressSystemStatsLegendsColors {
    NOTSTARTED = "rgba(120,136,158)",
    ONTRACK = "rgba(39,174,97)",
    COMPLETED = "rgba(0,94,184)",
    WATCHLIST = "rgba(248,167,106)",
    DELAYED = "rgba(235,87,87)",
}

export enum ProgressSystemDonutChartColors {
    NOTSTARTED = "rgba(120,136,158, 0.6)",
    ONTRACK = "rgba(39,174,97, 0.6)",
    COMPLETED = "rgba(0,94,184, 0.6)",
    WATCHLIST = "rgba(248,167,106, 0.6)",
    DELAYED = "rgba(235,87,87, 0.6)",
}

export const PROGRESS_SYSTEM_STATS_NAMES = {
    NOTSTARTED: "Not-Started (No Delay)",
    ONTRACK: "On-Track",
    COMPLETED: "Completed",
    WATCHLIST: "Watchlist",
    DELAYED: "Delayed",
};

export function getProgressSystemDonutChartColors(status: string | null | undefined): string | undefined {
    if (!status) return undefined;
    return ProgressSystemDonutChartColors[status.toUpperCase() as keyof typeof ProgressSystemDonutChartColors];
}

export function getProgressSystemStatsLegendsColors(status: string | undefined): string {
    if (!status) return ProgressSystemStatsLegendsColors.NOTSTARTED;
    return (
        ProgressSystemStatsLegendsColors[status.toUpperCase() as keyof typeof ProgressSystemStatsLegendsColors] ||
        ProgressSystemStatsLegendsColors.NOTSTARTED
    );
}

export enum ProgressBaseBaseColor {
    NOTSTARTED = "rgba(120,136,158, 0.2)",
    ONTRACK = "rgba(39,174,97, 0.2)",
    COMPLETED = "rgba(0,94,184, 0.2)",
    WATCHLIST = "rgba(248,167,106, 0.2)",
    DELAYED = "rgba(235,87,87, 0.2)",
}

export function getProgressBaseBaseColor(status: string | null | undefined): string {
    if (!status) {
        return ProgressBaseBaseColor.NOTSTARTED;
    }
    return ProgressBaseBaseColor[status.toUpperCase() as keyof typeof ProgressBaseBaseColor] || ProgressBaseBaseColor.NOTSTARTED;
}

export enum ProgressColos {
    sprint,
    meetings,
    tasks,
}

export class CustomProgressBarData {
    iconName: string;
    baseUtilizationWidth: number;
    barLabel: string;
    utilizedBarColor: string;
    baseBarColor: string;
}

export enum MyViewProgressBarsColors {
    SPRINTS_BASE = "#F8A76A20",
    SPRINTS_MAIN = "#F8A76A",
    MEEINGS_BASE = "#EB575720",
    MEETING_MAIN = "#EB5757",
    OTHER_TASKS_BASE = "#005EB820",
    OTHER_TASKS_MAIN = "#005EB8",
}

export enum TodayFocusTextColors {
    TASKS = "#27AE61",
    MEETINGS = "#EB5757",
    DELAYED = "#F8A76A",
    WATCHLIST = "#005EB8",
    DEPENDENCIES = "#8B8D9B",
}

export class TodayFocusExtendedData {
    prefixText: string = "";
    textToShow: string;
    textColor: string;
    suffixText: string = "";
    itemCount: number;
    id: number;
}

export class SearchDialogData {
    sprintId: number | null;
    teamId: number | null;
    orgId: number | null;
    isTaskScreen: boolean | null = false;
    isTopNavbarSearch: boolean | null = false;
    epicId: number | null = null;
    filterWorkItemsList: string[] | null = [];
    projectId: number | null = null;
}

export class CustomSelectOption {
    value: any;
    label: string;
}

export enum MessageTypeEnum {
    SUCCESS = "success",
    ERROR = "error",
    INFO = "info",
}

export type MessageType = MessageTypeEnum;
export const VALID_POSITIVE_NUMBER_REGEX = "^[1-9]\\d*$";

export interface State<T> {
    loading: boolean;
    data: T | null;
    error: any | null;
    paginationLoading?: boolean;
    paginationError?: any | null;
    // Optional: a flag to know what caused the last update
    lastUpdateType?: LastUpdateType | null;
}

export enum LastUpdateType {
    INITIAL = "INITIAL",
    PAGINATION = "PAGINATION",
    NEW_MESSAGE = "NEW_MESSAGE",
    DELETE_MESSAGE = "DELETE_MESSAGE",
    EDIT_MESSAGE = "EDIT_MESSAGE",
    JUMP_TO_MESSAGE = "JUMP_TO_MESSAGE",
}

export interface DropDownOption<valueType> {
    label: string;
    secondary_label: string;
    label_extended?: string;
    avatar: string | null;
    isInactive: boolean;
    value: valueType | null;
    selected?: boolean;
    isSelectable?: boolean;
    tooltip?: string;
}

export type ProfileFetchTyp = ProfileFetchType.USER_ACCOUNT_ID | ProfileFetchType.USER_EMAIL;

export enum ProfileFetchType {
    USER_ACCOUNT_ID = 1,
    USER_EMAIL = 2,
}
